import React, { useState } from "react";
import { makeStyles, useTheme, Button, IconButton } from "@seahorse/ui";
import { ChevronDown, ChevronUp } from "lucide-react";

interface FAQItem {
  question: string;
  answer: string;
}

const FaqTile = () => {
  const theme = useTheme();

  const useStyles = makeStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      gap: "24px",
      padding: "16px 0",
      backgroundColor: theme.globallink.colors.DarkGrey[500],

      width: "100%",
      height: "100%",
      //   alignItems: "center",
    },
    section: {
      padding: "16px",
      backgroundColor: theme.palette.background.paper,
      borderRadius: "8px",
      boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
    },
    sectionHeader: {
      fontSize: "28px",
      fontWeight: "bold",

      color: theme.palette.primary.main,
      marginBottom: "24px",
    },
    questionRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 0",
      borderBottom: `1px solid ${theme.palette.divider}`,
      cursor: "pointer",
    },
    questionText: {
      fontWeight: 600,
    },
    answer: {
      padding: "8px 8px 16px 8px",
      fontSize: "16px",
      lineHeight: 1.6,
      backgroundColor: "#f5f7fa", // soft gray-blue or light tone
      borderRadius: "4px",
      color: "#374151",
    },
    footer: {
      marginTop: "16px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "8px",
    },
    footerText: {
      fontSize: "16px",
    },
    faqListContainer: {
      maxHeight: "80vh", // or "50vh" for viewport-relative
      overflowY: "auto",
      paddingRight: "4px",

      // Optional scrollbar customization (WebKit browsers)
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.divider,
        borderRadius: "4px",
      },
    },
  });

  const classes = useStyles();

  const faqList: FAQItem[] = [
    {
      question: "What is CoTrader?",
      answer:
        "CoTrader is EMAlpha’s AI-powered tool designed to help FX traders stay informed through real-time, localized insights.",
    },
    {
      question: "How is EMAlpha different?",
      answer:
        "EMAlpha focuses on multilingual content, offering insights from non-English sources that are often overlooked.",
    },
    {
      question: "Is there support available?",
      answer:
        "Yes, you can reach out to us anytime via the contact details below.",
    },
    {
      question: "What is the Spot Tile?",
      answer:
        "The Spot Tile provides real-time FX spot rate data and trading workflow tools. It allows users to monitor, execute, and manage FX spot trades directly from a streamlined interface.",
    },
    {
      question: "How frequently are spot prices updated?",
      answer:
        "Spot prices are updated in real-time using direct feeds from liquidity providers, ensuring minimal latency and high accuracy.",
    },

    // Spot Chat Tile
    {
      question: "What is the Spot Chat Tile?",
      answer:
        "The Spot Chat Tile combines market insights with conversational UI. It lets users interact with market data, AI agents, and team members through an intuitive chat experience.",
    },
    {
      question: "Can I use commands in the Spot Chat?",
      answer:
        "Yes, you can type structured commands or ask natural language questions to retrieve FX rates, summaries, trade details, and more.",
    },
    {
      question: "Is the Spot Chat Tile integrated with Spot Tile?",
      answer:
        "Yes, it’s context-aware and can retrieve or summarize data from the Spot Tile and other tools in real-time.",
    },

    // Forward Tile
    {
      question: "What is the Forward Tile?",
      answer:
        "The Forward Tile provides tools for managing FX forward contracts. It helps track forwards, calculate settlements, and analyze curves.",
    },
    {
      question: "Can I simulate forward pricing?",
      answer:
        "Yes, the Forward Tile allows forward rate simulations using customizable market parameters like spot rate, interest differentials, and tenor.",
    },
    {
      question: "Does it support NDF and deliverable forwards?",
      answer:
        "Yes, both deliverable and non-deliverable forwards (NDFs) are supported, including custom tenor configurations.",
    },

    // FXGPT
    {
      question: "What is FXGPT?",
      answer:
        "FXGPT is a generative AI assistant designed specifically for FX traders. It answers questions, explains market movements, summarizes macro events, and helps automate trade workflows.",
    },
    {
      question: "How does FXGPT get its information?",
      answer:
        "FXGPT is powered by multilingual AI agents and large language models trained on real-time news, macroeconomic data, and trade activity sourced from verified global feeds.",
    },
    {
      question: "Can I ask FXGPT to draft trade justifications?",
      answer:
        "Absolutely. FXGPT can generate trade rationales, summaries for compliance, and structured reports based on your input and market data.",
    },
  ];

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleIndex = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  const handleContactClick = () => {
    window.location.href = "mailto:support@emalpha.com";
  };

  return (
    <div className={classes.root}>
      <div className={classes.section}>
        <div className={classes.sectionHeader}>Frequently Asked Questions</div>

        <div className={classes.faqListContainer}>
          {faqList.map((faq, index) => (
            <div key={index}>
              <div
                className={classes.questionRow}
                onClick={() => toggleIndex(index)}
              >
                <div className={classes.questionText}>{faq.question}</div>
                <i
                  className={`fa ${
                    openIndex === index ? "fa-chevron-up" : "fa-chevron-down"
                  }`}
                  style={{
                    fontSize: "16px",
                    marginLeft: "8px",
                    cursor: "pointer",
                  }}
                />
              </div>
              {openIndex === index && (
                <div className={classes.answer}>{faq.answer}</div>
              )}
            </div>
          ))}
        </div>

        <div className={classes.footer}>
          <div className={classes.footerText}>Still have an issue?</div>
          <Button variant="contained" onClick={handleContactClick}>
            Contact Us
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FaqTile;
